import { Modal } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import Select, { components as SelectComponents } from "react-select";
import { Doughnut } from "react-chartjs-2";
import { toast } from "sonner";

import {
  getRoles,
  getAllStates,
  getAllCities,
} from "../../../redux/slices/authSlice";
import { filterCity } from "../../../redux/slices/searchSlice";
import { allowedNames, allowedRolesForFree } from "../../../constants";
import ComparisonModal from "../../restriction-modals/ComparisonModal";
import RestrictionModal from "../../restriction-modals/RestrictionModal";
import {
  savePaycheck,
  listPaychecks,
  removePaycheck,
  viewPaycheck,
  updatePaycheck,
  calculatePaycheck,
} from "../../../redux/slices/paycheckSlice";
import paycheckService from "../../../api/paycheckService";
import NoIndexSEO from "../../../components/SEO/NoIndexSEO";
import { seoConfig } from "../../../utils/seoConfig";
import {
  activityStart,
  activityEnd,
} from "../../../redux/slices/trackActivitySlice";
import usePageTracking from "../../../hooks/usePageTracking";
import { UserRound } from "lucide-react";
import {
  PROFILE_COMPLETION_FIELDS,
  getProfileCompletionPercent,
} from "../../../utils/profileCompletion";
import { useTheme } from "../../../context/ThemeContext";
import "./paycheck.scss";

/* ── Config ─────────────────────────────────────────────────── */

const PROFILE_COMPLETION_FIELDS_BY_KEY = Object.fromEntries(
  PROFILE_COMPLETION_FIELDS.map((field) => [field.key, field]),
);

// Priority-ordered: the first of these still incomplete is the one shown,
// since it's the field most likely to affect the paycheck defaults/comparison.
const PAYCHECK_RELEVANT_PROFILE_NUDGES = [
  {
    key: "role",
    message: "Add your nursing role to auto-fill accurate paycheck defaults.",
  },
  {
    key: "city",
    message: "Add your city and state to auto-fill your paycheck defaults.",
  },
  {
    key: "salary",
    message: "Add your preferred salary to compare against your take-home pay.",
  },
];

const PAY_TYPES = [
  { value: "salary", label: "Salary" },
  { value: "hourly", label: "Hourly" },
];

const PAY_FREQUENCIES = [
  { value: "weekly", label: "Weekly (52 paychecks/year)", per: 52 },
  { value: "biweekly", label: "Bi-weekly (26 paychecks/year)", per: 26 },
  { value: "semimonthly", label: "Semi-monthly (24 paychecks/year)", per: 24 },
  { value: "monthly", label: "Monthly (12 paychecks/year)", per: 12 },
  { value: "annual", label: "Annually (1 paycheck/year)", per: 1 },
];

const FILING_STATUSES = [
  { value: "single", label: "Single" },
  { value: "mfj", label: "Married filing jointly" },
  { value: "hoh", label: "Head of household" },
];

const PAY_BY_API = {
  weekly: "Week",
  biweekly: "Bi-Weekly",
  semimonthly: "Semi-Monthly",
  monthly: "Month",
  annual: "Year",
};

const PAY_BY_FROM_API = {
  Week: "weekly",
  Weekly: "weekly",
  "Bi-Weekly": "biweekly",
  Biweekly: "biweekly",
  "Semi-Monthly": "semimonthly",
  Semimonthly: "semimonthly",
  Month: "monthly",
  Monthly: "monthly",
  Year: "annual",
  Yearly: "annual",
  Annual: "annual",
  Annually: "annual",
};

const DEDUCTION_API_META = {
  k401: { apiKey: "401k", ficaExempt: false },
  hsa: { apiKey: "HSA", ficaExempt: true },
  health: { apiKey: "Health Insurance", ficaExempt: true },
  fsa: { apiKey: "FSA", ficaExempt: true },
};

const DEDUCTION_FROM_API = Object.entries(DEDUCTION_API_META).reduce(
  (acc, [k, meta]) => {
    acc[meta.apiKey] = k;
    return acc;
  },
  {},
);

const FILING_FROM_API = FILING_STATUSES.reduce((acc, f) => {
  acc[f.label] = f.value;
  return acc;
}, {});

const FREQ_PER_COUNT = PAY_FREQUENCIES.reduce((acc, f) => {
  acc[f.value] = f.per;
  return acc;
}, {});
const currentYear = new Date().getFullYear();

const PRE_TAX_DEDUCTIONS = [
  {
    key: "k401",
    label: "401(k) contribution",
    help: `${currentYear} max: $24,500 ($8,000 catch-up if 50+).`,
  },
  {
    key: "hsa",
    label: "HSA contribution",
    // Help text will be dynamic based on filing status - see component render
    help: null,
  },
  {
    key: "health",
    label: "Health insurance premium",
    help: "Your share of the monthly premium.",
  },
  {
    key: "fsa",
    label: "FSA contribution",
    help: `${currentYear} max: $3,400 / year.`,
  },
];

// IRS 2026 annual contribution limits used to cap the pre-tax deduction
// inputs. The form collects $/paycheck, so the per-paycheck cap is the
// annual limit ÷ paychecks-per-year.
// Note: HSA caps are filing-status dependent (see getHsaCap function)
const ANNUAL_CONTRIBUTION_CAPS = {
  k401: 24500,
  fsa: 3400,
};

// HSA annual contribution limits based on filing status
// Single = individual coverage, MFJ/HOH = family coverage
const HSA_CAPS = {
  single: 4400, // Individual coverage
  mfj: 8750, // Family coverage (Married filing jointly)
  hoh: 8750, // Family coverage (Head of household)
};

/* ── Helpers ────────────────────────────────────────────────── */
const formatCurrency = (v) =>
  v == null || isNaN(v)
    ? "$0"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(v);

const formatCurrency2 = (v) =>
  v == null || isNaN(v)
    ? "$0.00"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      }).format(v);

const parseNum = (v) => parseFloat(String(v ?? "").replace(/[^\d.]/g, "")) || 0;

const EMPTY_CALC = {
  annualGross: 0,
  grossPaycheck: 0,
  fed: 0,
  state: 0,
  local: 0,
  ss: 0,
  medicare: 0,
  sdi: 0,
  preTax: 0,
  postTax: 0,
  totalTaxes: 0,
  takeHome: 0,
  fedAnnual: 0,
  stateAnnual: 0,
  localAnnual: 0,
  ssAnnual: 0,
  medicareAnnual: 0,
  sdiAnnual: 0,
  preTaxAnnual: 0,
  totalTaxAnnual: 0,
  netAnnual: 0,
  fedPct: 0,
  statePct: 0,
  localPct: 0,
  ssPct: 0,
  medicarePct: 0,
  sdiPct: 0,
  takeHomePct: 0,
  effectivePct: 0,
};

const num = (v) => {
  if (v == null || v === "") return 0;
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

const mapCalculation = (api) => {
  if (!api || typeof api !== "object") return { ...EMPTY_CALC };
  return {
    annualGross: num(api.grossAnnual),
    grossPaycheck: num(api.grossPerPaycheck),
    fed: num(api.federalTaxPerPaycheck),
    state: num(api.stateTaxPerPaycheck),
    local: num(api.localTaxPerPaycheck),
    ss: num(api.socialSecurityPerPaycheck),
    medicare: num(api.medicarePerPaycheck),
    sdi: num(api.stateInsurancePerPaycheck),
    preTax: num(api.preTaxPerPaycheck),
    postTax: num(api.postTaxPerPaycheck),
    totalTaxes: num(api.totalTaxesPerPaycheck),
    takeHome: num(api.netPerPaycheck),

    fedAnnual: num(api.federalTaxAnnual),
    stateAnnual: num(api.stateTaxAnnual),
    localAnnual: num(api.localTaxAnnual),
    ssAnnual: num(api.socialSecurityTaxAnnual),
    medicareAnnual: num(api.medicareTaxAnnual),
    sdiAnnual: num(api.stateInsuranceTaxAnnual),
    preTaxAnnual: num(api.preTaxDeductionsAnnual),
    totalTaxAnnual: num(api.totalTaxesAnnual),
    netAnnual: num(api.netAnnual),

    fedPct: num(api.federalTaxPercentage),
    statePct: num(api.stateTaxPercentage),
    localPct: num(api.localTaxPercentage),
    ssPct: num(api.socialSecurityPercentage),
    medicarePct: num(api.medicarePercentage),
    sdiPct: num(api.stateInsurancePercentage),
    takeHomePct: num(api.takeHomePercentage),
    effectivePct: num(api.effectiveTaxRate),
  };
};

const formatSavedDate = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

/* ── react-select styling ───────────────────────────────────── */
// The menu is portaled to document.body, outside .mmp-paycheck's scope, so
// its page-local --brand-*/--fg-* tokens wouldn't resolve there. The global
// --mmp-* tokens (defined on :root / [data-theme]) do reach the portal.
const T = {
  brandInk: "var(--mmp-text-primary)",
  brand500: "var(--mmp-accent)",
  border: "var(--mmp-border)",
  fg3: "var(--mmp-text-secondary)",
  fg4: "var(--mmp-text-secondary)",
};

const selectStyles = {
  control: (b, s) => ({
    ...b,
    background: "var(--mmp-surface)",
    border: `1px solid ${s.isFocused ? T.brand500 : T.border}`,
    borderRadius: 10,
    minHeight: 50,
    boxShadow: s.isFocused ? "0 0 0 3px rgba(3,149,211,0.15)" : "none",
    padding: "0 14px",
    cursor: "pointer",
    "&:hover": { borderColor: s.isFocused ? T.brand500 : T.border },
  }),
  valueContainer: (b) => ({ ...b, padding: 0 }),
  singleValue: (b) => ({
    ...b,
    fontSize: 15,
    fontWeight: 700,
    color: T.brandInk,
    margin: 0,
  }),
  input: (b) => ({ ...b, margin: 0, padding: 0, color: T.brandInk }),
  placeholder: (b) => ({ ...b, color: T.fg4, margin: 0 }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (b, s) => ({
    ...b,
    color: T.fg3,
    padding: 0,
    transform: s.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 180ms cubic-bezier(0.2,0.8,0.2,1)",
    svg: { width: 18, height: 18 },
  }),
  menu: (b) => ({
    ...b,
    background: "var(--mmp-surface)",
    borderRadius: 10,
    marginTop: 4,
    padding: 4,
    boxShadow: "0 8px 20px rgba(15,23,42,0.10), 0 0 0 1px rgba(15,23,42,0.05)",
    zIndex: 1000,
  }),
  menuPortal: (b) => ({ ...b, zIndex: 9500 }),
  menuList: (b) => ({ ...b, padding: 0, maxHeight: 240 }),
  option: (b, s) => ({
    ...b,
    fontSize: 13,
    fontWeight: s.isSelected ? 700 : 500,
    borderRadius: 6,
    padding: "7px 10px",
    margin: 0,
    cursor: "pointer",
    opacity: 1,
    color: s.isDisabled ? "var(--mmp-text-secondary)" : s.isSelected ? "#fff" : T.brandInk,
    background: s.isSelected
      ? T.brand500
      : s.isFocused && !s.isDisabled
      ? "var(--mmp-accent-bg)"
      : "transparent",
  }),
};

/* ── Small components ───────────────────────────────────────── */

function ChevronDown() {
  return (
    <svg
      viewBox="0 0 20 20"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="5 8 10 13 15 8" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
    >
      <circle cx="10" cy="10" r="8" />
      <line x1="10" y1="9" x2="10" y2="14" />
      <circle cx="10" cy="6.5" r="0.6" fill="currentColor" />
    </svg>
  );
}

// Small "i" affordance shown next to a heading or a value that's produced by
// a formula. The explanation appears in a CSS bubble on hover/focus; `title`
// is the no-JS/touch fallback, and aria-label keeps it screen-reader friendly.
// No tooltip library is used anywhere in this app, so this stays dependency-free.
function InfoTip({ text }) {
  return (
    <span
      className="pc-infotip"
      tabIndex={0}
      role="img"
      aria-label={text}
      title={text}
    >
      <InfoIcon />
      <span className="pc-infotip-bubble" role="tooltip">
        {text}
      </span>
    </span>
  );
}

// Format a raw numeric string ("100000.5") for display ("100,000.5") while
// preserving the trailing dot so the user can keep typing a decimal.
function formatWithCommas(raw) {
  if (raw === "" || raw == null) return "";
  const str = String(raw);
  const [intPart, decPart] = str.split(".");
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decPart !== undefined ? `${withCommas}.${decPart}` : withCommas;
}

// $999,999,999 — capped here AND server-side (paycheck_calculator.js,
// paycheck_scenario.js Joi schemas). Above this the per-paycheck snapshots
// stored in paycheck_scenarios overflow DECIMAL(15,2) and MySQL rejects the
// save. Realistic salaries top out well below this; the cap doubles as a
// guardrail against typo-prone "1000000000" pastes.
const MAX_SALARY = 999999999;

// Strip everything that isn't a digit or a decimal point, collapse
// multiple dots to the first one, and clamp the numeric value to MAX_SALARY
// so the user can't type past the cap. Trailing dots are preserved so
// "1234." stays editable mid-decimal.
function parseNumeric(input) {
  let cleaned = String(input).replace(/[^\d.]/g, "");
  const firstDot = cleaned.indexOf(".");
  if (firstDot !== -1) {
    cleaned =
      cleaned.slice(0, firstDot + 1) +
      cleaned.slice(firstDot + 1).replace(/\./g, "");
  }
  if (cleaned === "" || cleaned === ".") return cleaned;
  const n = parseFloat(cleaned);
  if (Number.isNaN(n) || n <= MAX_SALARY) return cleaned;
  // Over the cap → snap back to the max. Preserves 2-decimal precision.
  return String(MAX_SALARY);
}

function MoneyField({ label, value, suffix, onChange, autoBig, dataTour }) {
  return (
    <div className="pc-field" data-tour={dataTour}>
      {label && <div className="pc-field-label">{label}</div>}
      <div className="pc-money-input">
        <span className="pc-money-prefix">$</span>
        <input
          type="text"
          inputMode="decimal"
          value={formatWithCommas(value)}
          onChange={(e) => onChange(parseNumeric(e.target.value))}
          className={autoBig ? "is-big" : ""}
          placeholder="0"
        />
        {suffix && <span className="pc-money-suffix">{suffix}</span>}
      </div>
    </div>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
  searchable = false,
  placeholder,
  customComponents,
  isOptionDisabled,
  dataTour,
}) {
  const selected = options.find((o) => o.value === value) || null;
  return (
    <div className="pc-field" data-tour={dataTour}>
      {label && <div className="pc-field-label">{label}</div>}
      <Select
        classNamePrefix="mmp-pc-sel"
        options={options}
        value={selected}
        onChange={(o) => o && onChange(o.value)}
        isSearchable={searchable}
        placeholder={
          placeholder || (searchable ? "Type to search…" : "Select…")
        }
        menuPlacement="auto"
        menuPosition="fixed"
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : null
        }
        styles={selectStyles}
        aria-label={label}
        {...(customComponents ? { components: customComponents } : {})}
        {...(isOptionDisabled ? { isOptionDisabled } : {})}
      />
    </div>
  );
}

function PayTypeToggle({ value, onChange, dataTour }) {
  return (
    <div className="pc-field" data-tour={dataTour}>
      <div className="pc-field-label">PAY TYPE</div>
      <div className="pc-toggle">
        {PAY_TYPES.map((t) => (
          <button
            key={t.value}
            type="button"
            className={`pc-toggle-btn ${value === t.value ? "is-active" : ""}`}
            onClick={() => onChange(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* Donut chart — react-chartjs-2 Doughnut with a centered label/sub overlay.
 * `hover` and `onHover` are lifted to the parent so legend ↔ chart highlight stays in sync. */
function Donut({
  segments,
  centerLabel,
  centerSub,
  formatValue,
  hover,
  onHover,
}) {
  const { resolvedTheme } = useTheme();
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const chartRef = useRef(null);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    if (hover != null && hover >= 0 && hover < segments.length) {
      const meta = chart.getDatasetMeta(0);
      const arc = meta?.data?.[hover];
      const pos = arc?.tooltipPosition ? arc.tooltipPosition() : { x: 0, y: 0 };
      chart.setActiveElements([{ datasetIndex: 0, index: hover }]);
      chart.tooltip.setActiveElements([{ datasetIndex: 0, index: hover }], pos);
    } else {
      chart.setActiveElements([]);
      chart.tooltip.setActiveElements([], { x: 0, y: 0 });
    }
    chart.update();
  }, [hover, segments.length]);

  const dimColor = (hex) => {
    if (typeof hex !== "string") return hex;
    if (hex.length === 7) return hex + "33"; // ~20% alpha
    if (hex.length === 4) return hex + "5";
    return hex;
  };

  const data = {
    labels: segments.map((s) => s.name),
    datasets: [
      {
        data: segments.map((s) => s.value),
        backgroundColor: segments.map((s, i) =>
          hover != null && hover !== i ? dimColor(s.color) : s.color,
        ),
        // Canvas fill/stroke colors can't read CSS custom properties, so
        // this has to be a literal hex matching --mmp-surface — otherwise
        // the white gap between slices reads as a glaring seam in dark mode.
        borderColor: segments.map(() =>
          resolvedTheme === "dark" ? "#121f26" : "#ffffff",
        ),
        borderWidth: 2,
        hoverOffset: 6,
        offset: segments.map((_, i) => (hover === i ? 10 : 0)),
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "70%",
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "rgba(14, 42, 71, 0.95)",
        padding: 10,
        titleFont: { weight: "700", size: 12 },
        bodyFont: { size: 12 },
        callbacks: {
          label: (ctx) => {
            const v = ctx.parsed;
            const pct = ((v / total) * 100).toFixed(1);
            return `${pct}% · ${formatValue ? formatValue(v) : v}`;
          },
        },
      },
    },
    onHover: (_evt, els) => {
      onHover(els && els.length ? els[0].index : null);
    },
  };

  return (
    <div className="pc-donut-box">
      <Doughnut ref={chartRef} data={data} options={options} />
      <div className="pc-donut-center-wrap" aria-hidden="true">
        <div className="pc-donut-center-label">{centerLabel}</div>
        <div className="pc-donut-center-sub">{centerSub}</div>
      </div>
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────── */
const Paycheck = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    dispatch(activityStart({ page_name: "PAYCHECK_CALCULATOR", platform: "web" }));
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(activityEnd({ page_name: "PAYCHECK_CALCULATOR" }));
    };
  }, [dispatch]);

  usePageTracking("PAYCHECK_CALCULATOR");
  const location = useLocation();
  const allStates = useSelector((s) => s.auth?.stateItems?.states || []);
  const isSaving = useSelector((s) => s.paycheck?.isLoading || false);
  const isListLoading = useSelector((s) => s.paycheck?.isListLoading || false);
  const savedPaychecksRaw = useSelector((s) => s.paycheck?.paycheckList || []);
  const paycheckPage = useSelector((s) => s.paycheck?.paycheckPage || 0);
  const paycheckHasMore = useSelector(
    (s) => s.paycheck?.paycheckHasMore || false,
  );
  // Total saved on the server (set from the list response). The chip-row
  // count was previously `savedPaychecks.length`, which grows as the user
  // scrolls and paginates more pages in — misleading because the message
  // is meant to convey the absolute saved count, not "what's loaded right now".
  const paycheckTotal = useSelector((s) => s.paycheck?.paycheckTotal || 0);
  const roleItems = useSelector((s) => s.auth?.roleItems);
  const planName = useSelector(
    (s) => s.auth?.user?.plan?.name || s.profile?.profileItem?.data?.plan?.name,
  );
  const profileData = useSelector((s) => s.profile?.profileItem?.data);

  // "Complete your profile" nudge shown above the title, in its own card —
  // picks the single highest-priority missing field rather than a generic
  // percentage, since that's more actionable here. Dismissal is in-memory
  // only (not persisted) so the nudge reappears on reload, re-login, or a
  // fresh tab — not just once ever.
  const [profileNudgeDismissed, setProfileNudgeDismissed] = useState(false);
  const profileCompletionPercent = getProfileCompletionPercent(profileData);
  const activeProfileNudge = profileData
    ? PAYCHECK_RELEVANT_PROFILE_NUDGES.find((nudge) => {
        const field = PROFILE_COMPLETION_FIELDS_BY_KEY[nudge.key];
        return field && !field.isComplete(profileData);
      })
    : null;
  const dismissProfileNudge = () => {
    setProfileNudgeDismissed(true);
  };

  const SAVED_LIMIT = 6;
  const savedScrollRef = useRef(null);
  const fetchingRef = useRef(false);
  const autoCalcTimerRef = useRef(null);

  const [payType, setPayType] = useState("salary");
  const [salary, setSalary] = useState("");
  const [hoursPerWeek, setHoursPerWeek] = useState("40");
  // Tracks the annual basis so changing hours recalculates the hourly rate.
  const hourlyAnnualRef = useRef(0);
  const [frequency, setFrequency] = useState("weekly");
  const [filing, setFiling] = useState("single");
  const [stateCode, setStateCode] = useState("");
  const [deductions, setDeductions] = useState({
    k401: "",
    hsa: "",
    health: "",
    fsa: "",
  });
  const [locationOpen, setLocationOpen] = useState(true);
  const [salaryOpen, setSalaryOpen] = useState(true);
  const [deductionsOpen, setDeductionsOpen] = useState(true);
  const [donutHover, setDonutHover] = useState(null);
  const [selectedSavedId, setSelectedSavedId] = useState(null);
  const [appliedSnapshot, setAppliedSnapshot] = useState(null);
  const [limitInfo, setLimitInfo] = useState(null);
  const [calcLimitInfo, setCalcLimitInfo] = useState(null);
  const [usageInfo, setUsageInfo] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  // Name-prompt modal shown before save. Prefill is `$<annualGross> paycheck · <date>`.
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState("");

  // Location + role filter
  const profileInitRef = useRef(false);
  const [pcRole, setPcRole] = useState(null);
  const [pcState, setPcState] = useState("");
  const [pcCity, setPcCity] = useState("");
  const [pcRoleOptions, setPcRoleOptions] = useState([]);
  const [pcApiStates, setPcApiStates] = useState([]);
  const [pcApiCities, setPcApiCities] = useState([]);
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
  const [upgradeSource, setUpgradeSource] = useState(null);
  const [upgradeText, setUpgradeText] = useState({
    header: "",
    desc: "",
    massage1: "",
    pluspricem: "",
    pluspricey: "",
    desc2: "",
    promassage: "",
    propricem: "",
    propricey: "",
  });

  const savedPaychecks = useMemo(
    () =>
      (savedPaychecksRaw || []).map((p, idx) => ({
        id: p?.id ?? p?._id ?? idx,
        customName: p?.custom_name || "",
        salary: parseNum(p?.gross_pay),
        frequency:
          // pay_by now stores machine codes ("weekly"); legacy rows with
          // labels ("Week") fall back to the back-map.
          FREQ_PER_COUNT[p?.pay_by] !== undefined
            ? p.pay_by
            : PAY_BY_FROM_API[p?.pay_by] || (p?.pay_by || "").toLowerCase(),
        payByLabel: p?.pay_by || "",
        takeHome: parseNum(p?.net_pay),
        date: formatSavedDate(p?.created_at || p?.createdAt || p?.updated_at),
        raw: p,
      })),
    [savedPaychecksRaw],
  );

  // Deep-link: when navigated here with `?load=<id>` (e.g. from the
  // "Your saved paychecks" profile section), auto-select the matching
  // scenario once the list arrives.
  useEffect(() => {
    const loadId = Number(new URLSearchParams(location.search).get("load"));
    if (!loadId) return;
    const match = savedPaychecks.find((p) => p.id === loadId);
    if (!match) return;
    handleViewPaycheck(match);
    // Clear the query param so a refresh doesn't trigger again.
    navigate("/salaries-paychecks?tab=paycheck", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, savedPaychecks]);

  // Dropdown options sourced from the API.
  const stateOptions = useMemo(
    () =>
      allStates.map((s) => ({
        value: s.state,
        label: s.state_full_name || s.state,
      })),
    [allStates],
  );

  const apiCalculation = useSelector((s) => s.paycheck?.calculation || null);
  // Coverage flags from the calculate response — drives row visibility so
  // the UI only shows what's backed by data (Phase 1 backend change).
  const coverage = apiCalculation?.coverage || null;
  const stateSupported = coverage?.state?.supported !== false;
  const stateInsuranceSupported = coverage?.stateInsurance?.supported === true;
  // Only some cities levy a local income tax (e.g. NYC, Philadelphia,
  // Detroit). The backend sets this true only when it found local brackets
  // for the selected work state + city, so we render the row only then.
  const localSupported = coverage?.localIncome?.supported === true;
  const localCity = coverage?.localIncome?.city || "";

  // Load states + roles once.
  useEffect(() => {
    if (!allStates.length) dispatch(getAllStates());
    dispatch(getRoles());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch the user's daily usage on mount so the banner renders accurate
  // counts before the first calculation. (After a successful calc, the
  // calculate response echoes a fresh usage block and we sync from that.)
  useEffect(() => {
    let cancelled = false;
    paycheckService
      .usage()
      .then((res) => {
        if (cancelled) return;
        setUsageInfo(res?.data?.data || null);
      })
      .catch(() => {
        // Non-auth or transient — leave banner in its default state.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Reset HSA contribution when filing status changes if it exceeds the new cap
  useEffect(() => {
    const currentHsa = parseFloat(deductions.hsa) || 0;
    if (currentHsa === 0) return; // No HSA entered yet

    const newCap = HSA_CAPS[filing] || HSA_CAPS.single;
    const perYear = FREQ_PER_COUNT[frequency];
    if (!perYear) return; // Frequency not set yet

    const maxPerPaycheck = Math.floor((newCap / perYear) * 100) / 100;

    // If current HSA exceeds new cap, reset it to the max allowed
    if (currentHsa > maxPerPaycheck) {
      setDeductions((prev) => ({ ...prev, hsa: String(maxPerPaycheck) }));
      toast.info(
        `HSA contribution adjusted to ${
          filing === "single" ? "individual" : "family"
        } coverage limit: $${maxPerPaycheck}/paycheck`,
      );
    }
  }, [filing, frequency, deductions.hsa]);

  // Keep usageInfo in sync with what the calculate API returns.
  useEffect(() => {
    if (apiCalculation?.usage) setUsageInfo(apiCalculation.usage);
  }, [apiCalculation]);

  // Load first page of saved paychecks on mount.
  useEffect(() => {
    dispatch(listPaychecks({ page: 1, limit: SAVED_LIMIT, append: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Populate pcApiStates from the already-loaded allStates list.
  useEffect(() => {
    if (allStates.length > 0) {
      setPcApiStates(
        allStates.map((s) => ({ name: s.state_full_name, code: s.state })),
      );
    }
  }, [allStates]);

  // Initialize role/state/city filters from user profile (once).
  useEffect(() => {
    if (profileInitRef.current || !profileData) return;
    profileInitRef.current = true;
    if (profileData?.nursing_role?.id) setPcRole(profileData.nursing_role.id);
    if (profileData?.living_state_info?.state_full_name)
      setPcState(profileData.living_state_info.state_full_name);
    if (profileData?.city) setPcCity(profileData.city);
  }, [profileData]);

  // Build role select options with plan-aware disabled flags.
  useEffect(() => {
    if (!roleItems?.describe_roles) return;
    const data = roleItems.describe_roles.map((s) => {
      const roleName = s.name;
      let isDisabled = false;
      let requiredPlan = "";
      if (planName === "Free") {
        if (!allowedRolesForFree.includes(roleName)) {
          isDisabled = true;
          requiredPlan = "Pro";
        }
      }
      return {
        value: s.id,
        label: roleName,
        isDisabled,
        requiredPlan,
        roleName,
      };
    });
    setPcRoleOptions(data);
  }, [roleItems, planName]);

  // Load cities whenever the filter state changes.
  useEffect(() => {
    if (!pcApiStates.length || !pcState) return;
    const stateData = pcApiStates.find((s) => s.name === pcState);
    setPcApiCities([]);
    if (!stateData) return;
    dispatch(getAllCities({ search_state: stateData.code }))
      .then((action) => {
        const cities =
          action.payload?.data?.cities || action.payload?.cities || [];
        setPcApiCities(cities.map((c) => c.city));
      })
      .catch(() => setPcApiCities([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pcState, pcApiStates]);

  // Auto-select first city when the state changes and current city isn't in the new list.
  useEffect(() => {
    if (pcApiCities.length > 0 && !pcApiCities.includes(pcCity)) {
      setPcCity(pcApiCities[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pcApiCities]);

  const pcApiStateCode = pcApiStates.find((s) => s.name === pcState)?.code;

  // Keep "Where you work" state in sync when the location filter state changes.
  useEffect(() => {
    if (pcApiStateCode) setStateCode(pcApiStateCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pcApiStateCode]);

  // Call filterCity when role + state + city are all set; pre-fill salary.
  useEffect(() => {
    if (!pcCity || !pcApiStateCode || !pcRole) return;
    dispatch(
      filterCity({
        city: [pcCity],
        states: [pcApiStateCode],
        role: [pcRole],
        page: 1,
        per_page: 1,
      }),
    ).then((result) => {
      const cityData = result?.payload?.cities?.[0];
      const median = cityData?.median_annual_salary;
      if (median) {
        const annualNum = Number(median);
        if (payType === "hourly") {
          hourlyAnnualRef.current = annualNum;
          setSalary(String((annualNum / 2080).toFixed(2)));
          setHoursPerWeek("40");
        } else {
          setSalary(String(Math.round(annualNum)));
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pcCity, pcApiStateCode, pcRole, dispatch]);

  const loadNextSavedPage = useCallback(() => {
    if (fetchingRef.current) return;
    if (!paycheckHasMore) return;
    fetchingRef.current = true;
    dispatch(
      listPaychecks({
        page: paycheckPage + 1,
        limit: SAVED_LIMIT,
        append: true,
      }),
    ).finally(() => {
      fetchingRef.current = false;
    });
  }, [dispatch, paycheckPage, paycheckHasMore]);

  const handleSavedScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      if (!el) return;
      const threshold = 80;
      if (
        el.scrollLeft + el.clientWidth >= el.scrollWidth - threshold &&
        paycheckHasMore &&
        !isListLoading
      ) {
        loadNextSavedPage();
      }
    },
    [paycheckHasMore, isListLoading, loadNextSavedPage],
  );

  const calc = useMemo(() => mapCalculation(apiCalculation), [apiCalculation]);

  // Each entry below mirrors one part of `isReady`. Keeping them as a
  // single ordered list lets us derive the disabled state AND the
  // human-readable warning text from one source of truth — so the two
  // can never drift apart.
  const missingFields = useMemo(() => {
    const missing = [];
    if (!(parseNum(salary) > 0)) missing.push("annual salary");
    if (!payType) missing.push("pay type");
    if (!frequency) missing.push("pay frequency");
    if (!filing) missing.push("filing status");
    if (!stateCode) missing.push("state");
    return missing;
  }, [salary, payType, frequency, filing, stateCode]);

  const isReady = missingFields.length === 0;

  // Build natural-language warning: "salary", "salary and state",
  // "salary, state and filing status". Comma-separated with an "and"
  // before the last item — matches how a person would speak it.
  const missingMsg = useMemo(() => {
    if (missingFields.length === 0) return "";
    if (missingFields.length === 1)
      return `Add ${missingFields[0]} to continue.`;
    const head = missingFields.slice(0, -1).join(", ");
    const tail = missingFields[missingFields.length - 1];
    return `Add ${head} and ${tail} to continue.`;
  }, [missingFields]);

  // Per-button so the warning appears next to whichever action the user
  // just attempted (Calculate / Save are in different columns).
  const [showCalcMissing, setShowCalcMissing] = useState(false);
  const [showSaveMissing, setShowSaveMissing] = useState(false);

  const handleCalculate = () => {
    if (!isReady) {
      toast.info("Please fill in all required fields first.");
      return;
    }
    setIsCalculating(true);
    // Send the per-paycheck values exactly as the user typed them and let
    // the backend annualise via `deductionUnit: "perPaycheck"`. This keeps
    // the request payload aligned with the on-screen `/paycheck` inputs.
    dispatch(
      calculatePaycheck({
        payType,
        // For hourly with custom hours, adjust the rate so that the backend's
        // (rate × 2080) formula yields the user's intended annual
        // (hourlyRate × hoursPerWeek × 52).
        payAmount:
          payType === "hourly"
            ? parseNum(salary) * (parseNum(hoursPerWeek) / 40)
            : parseNum(salary),
        payFrequency: frequency,
        state: stateCode,
        // City drives local (city) income tax for cities that levy one.
        city: pcCity,
        filingStatus: filing,
        retirement401k: parseNum(deductions.k401),
        healthInsurance: parseNum(deductions.health),
        hsaContribution: parseNum(deductions.hsa),
        fsaContribution: parseNum(deductions.fsa),
        deductionUnit: "perPaycheck",
      }),
    )
      .unwrap()
      .catch((err) => {
        if (err?.errorType === "PAYCHECK_CALCULATOR_LIMIT_REACHED") {
          setCalcLimitInfo({
            planSlug: err?.data?.plan_slug || "free",
            used: isFree ? FREE_DISPLAY_LIMIT : err?.data?.used ?? 1,
            limit: isFree ? FREE_DISPLAY_LIMIT : err?.data?.limit ?? 1,
            usageDate: err?.data?.usage_date || "",
          });
        } else {
          toast.error(err?.message || "Failed to calculate paycheck");
        }
      })
      .finally(() => setIsCalculating(false));
  };

  // Plan flags driven by the real usage response when available.
  const planSlug = usageInfo?.plan_slug || "free";
  const isFree = planSlug === "free" || planSlug === "free-trial";
  const limitReached = Boolean(usageInfo?.limit_reached);

  // Free users get a backend allowance of 2/day, but the first calc is
  // auto-spent on page load. We hide that and present a 1/day limit: subtract
  // the load calc from the displayed "used" so the banner/modal read 0→1 of 1
  // (never exposing the real backend cap of 2). `limitReached` still comes from
  // the backend, so the modal/lock fire at the true cap.
  const FREE_DISPLAY_LIMIT = 1;
  const displayLimit = isFree ? FREE_DISPLAY_LIMIT : usageInfo?.limit ?? 0;
  const displayUsed = isFree
    ? Math.min(FREE_DISPLAY_LIMIT, Math.max(0, (usageInfo?.used ?? 0) - 1))
    : usageInfo?.used ?? 0;

  // Tapping the button after the daily cap should surface the upgrade modal,
  // not silently no-op. Mirrors how the 403 path opens the same modal.
  const handlePayTypeChange = (newType) => {
    if (newType === payType) return;
    if (newType === "hourly" && payType === "salary") {
      const annual = parseNum(salary);
      if (annual > 0) {
        hourlyAnnualRef.current = annual;
        setSalary(String((annual / 2080).toFixed(2)));
        setHoursPerWeek("40");
      }
    } else if (newType === "salary" && payType === "hourly") {
      const hrs = parseNum(hoursPerWeek) || 40;
      const hourly = parseNum(salary);
      if (hourly > 0) {
        setSalary(String(Math.round(hourly * hrs * 52)));
      }
    }
    setPayType(newType);
  };

  const handleCalcClick = () => {
    if (limitReached) {
      setCalcLimitInfo({
        planSlug,
        used: isFree ? FREE_DISPLAY_LIMIT : usageInfo?.used ?? 1,
        limit: isFree ? FREE_DISPLAY_LIMIT : usageInfo?.limit ?? 1,
        usageDate: usageInfo?.usage_date || "",
      });
      return;
    }
    if (!isReady) {
      setShowCalcMissing(true);
      return;
    }
    handleCalculate();
  };

  // Auto-calculate whenever all required fields are filled.
  // 600ms debounce prevents a call on every salary keystroke.
  // Mirrors handleCalcClick: if the daily limit is already hit, show the
  // restriction modal instead of silently doing nothing.
  useEffect(() => {
    if (!isReady || isCalculating) return;
    // Auto-calculate for everyone, including Free users. Free users have a
    // backend allowance of 2/day: the first calc is auto-spent on page load
    // (inputs are prefilled), leaving exactly one for their own interaction.
    // The UI presents this as a 1/day limit (see displayUsed/displayLimit) so
    // the hidden load calc isn't visible to the user.
    if (autoCalcTimerRef.current) clearTimeout(autoCalcTimerRef.current);
    autoCalcTimerRef.current = setTimeout(() => {
      if (limitReached) {
        setCalcLimitInfo({
          planSlug,
          used: isFree ? FREE_DISPLAY_LIMIT : usageInfo?.used ?? 1,
          limit: isFree ? FREE_DISPLAY_LIMIT : usageInfo?.limit ?? 1,
          usageDate: usageInfo?.usage_date || "",
        });
        return;
      }
      handleCalculate();
    }, 600);
    return () => {
      if (autoCalcTimerRef.current) clearTimeout(autoCalcTimerRef.current);
    };
    // NOTE: `limitReached` is intentionally NOT a dependency. A successful
    // calculation flips it false→true (used 1 of 1); if it were a dep, that
    // flip would re-run this effect and pop the limit modal right after the
    // first calc. Excluding it means the effect only re-runs on real input
    // changes — each of which re-reads the current `limitReached` — so the
    // modal shows on the next attempt, not immediately after a success.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    salary,
    hoursPerWeek,
    payType,
    frequency,
    filing,
    stateCode,
    pcCity,
    deductions,
    isReady,
  ]);

  // When hours per week changes in hourly mode, keep the annual constant and
  // recalculate the hourly rate: rate = annual ÷ (newHours × 52).
  // useEffect(() => {
  //   if (payType !== "hourly") return;
  //   const annual = hourlyAnnualRef.current;
  //   const hrs = parseNum(hoursPerWeek);
  //   if (!annual || !hrs) return;
  //   setSalary(String((annual / (hrs * 52)).toFixed(2)));
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [hoursPerWeek]);

  const buildPaycheckPayload = () => {
    const stateLabel =
      stateOptions.find((s) => s.value === stateCode)?.label || stateCode;

    const apiDeductions = Object.entries(deductions).reduce((acc, [k, v]) => {
      const amount = parseNum(v);
      if (!amount) return acc;
      const meta = DEDUCTION_API_META[k];
      if (!meta) return acc;
      acc[meta.apiKey] = {
        type: "fixed",
        value: amount,
        ficaExempt: meta.ficaExempt,
      };
      return acc;
    }, {});

    // Annualise the user's input so the saved scenario stores the exact
    // value the user typed, not a per-paycheck derivation. For hourly
    // mode: rate × 40 hrs × 52 weeks = annual equivalent.
    const rawSalary = parseNum(salary);
    const annualGrossSalary =
      payType === "hourly"
        ? rawSalary * parseNum(hoursPerWeek) * 52
        : rawSalary;

    // Send machine codes (matching the calculate endpoint's enums) so save
    // and calculate share one shape.
    return {
      custom_name: stateLabel,
      state: stateCode,
      // Persist the work city so local (city) income tax can be re-applied
      // on reload — without it, the saved scenario recalculates with no city
      // and the local-tax line disappears.
      city: pcCity || null,
      pay_type: payType,
      pay_by: frequency,
      filing_status: filing,
      deductions: apiDeductions,
      // Source of truth: the annual gross. Aligns with the calculate
      // endpoint's `payAmount` field and avoids the per-paycheck →
      // annual round-trip drift that affected gross_pay-derived reloads.
      annual_gross_salary: annualGrossSalary || null,
      // Snapshot the computed per-paycheck totals so the chip-list display
      // can show take-home without recomputing.
      gross_pay: calc.grossPaycheck || null,
      net_pay: calc.takeHome || null,
    };
  };

  // Build the default name shown in the save-name modal. Format mirrors
  // the spec: "$79,040 paycheck · May 28, 2026". For hourly payType we
  // annualise (rate × 40 × 52) so the number on the chip stays consistent
  // with the calculator's annualised gross figure.
  const buildDefaultName = () => {
    const rawSalary = parseNum(salary);
    const annual =
      payType === "hourly"
        ? rawSalary * parseNum(hoursPerWeek) * 52
        : rawSalary;
    const annualLabel = annual
      ? `$${Math.round(annual).toLocaleString()}`
      : "$0";
    const today = new Date().toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    return `${annualLabel} paycheck · ${today}`;
  };

  // Open the name-prompt modal. Called by both "Save paycheck" (no scenario
  // selected) and "Save as new" (selected + dirty).
  const openSaveNameModal = () => {
    if (!isReady) {
      setShowSaveMissing(true);
      return;
    }
    setNameDraft(buildDefaultName());
    setNameModalOpen(true);
  };

  const handleSavePaycheck = () => {
    if (!isReady) {
      toast.info("Please fill in all required fields first.");
      return;
    }
    const trimmed = (nameDraft || "").trim();
    const payload = {
      ...buildPaycheckPayload(),
      custom_name: trimmed || buildDefaultName(),
    };

    dispatch(savePaycheck(payload))
      .unwrap()
      .then((res) => {
        setNameModalOpen(false);
        // Custom toast: confirmation + "View all saved paychecks" link.
        const savedName = payload.custom_name;
        // sonner's native `description` + `action` props give the
        // premium two-line layout (title + CTA) without custom JSX.
        toast.success(`Saved as "${savedName}"`, {
          description: "Find it on your profile anytime.",
          action: {
            label: "View all",
            // Navigate, then scroll the saved-paychecks card into view
            // after the route mounts. setTimeout sits after ScrollToTop
            // (which fires on every pathname change) so the scroll
            // doesn't get clobbered.
            onClick: () => {
              navigate("/profile#saved-paychecks");
              setTimeout(() => {
                const el = document.getElementById("saved-paychecks");
                if (el)
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 150);
            },
          },
        });
        // BE wraps the new row under `data.scenario`; mark it selected so the
        // action button flips from "Save" → "Saved" without a list reload.
        const newId =
          res?.savedPaycheck?.scenario?.id || res?.savedPaycheck?.id;
        if (newId) {
          setSelectedSavedId(newId);
          setAppliedSnapshot(currentSnapshot);
        }
        if (savedScrollRef.current) savedScrollRef.current.scrollLeft = 0;
        dispatch(listPaychecks({ page: 1, limit: SAVED_LIMIT, append: false }));
      })
      .catch((err) => {
        if (err?.errorType === "PAYCHECK_SCENARIO_LIMIT_REACHED") {
          setNameModalOpen(false);
          setLimitInfo({
            planSlug: err?.data?.plan_slug || "free",
            planLimit: err?.data?.plan_limit ?? 1,
            totalSaved: err?.data?.total_saved ?? 0,
            limitType: err?.data?.limit_type || "total",
            dailyLimit: err?.data?.daily_limit ?? 1,
            usedToday: err?.data?.used_today ?? 1,
          });
          return;
        }
        // Validation errors put the human-readable detail in `data`
        // (e.g. `"annual_gross_salary" must be less than or equal to ...`)
        // while `message` is just the generic "Validation Error".
        const detail =
          typeof err?.data === "string" && err.data.trim() ? err.data : null;
        toast.error(detail || err?.message || "Failed to save paycheck");
      });
  };

  const handleUpdatePaycheck = () => {
    if (!selectedSavedId) return;
    if (!isReady) {
      toast.info("Please fill in all required fields first.");
      return;
    }
    const payload = buildPaycheckPayload();

    dispatch(updatePaycheck({ id: selectedSavedId, data: payload }))
      .unwrap()
      .then((res) => {
        toast.success(res?.message || "Paycheck updated successfully!");
        setAppliedSnapshot(currentSnapshot);
        dispatch(listPaychecks({ page: 1, limit: SAVED_LIMIT, append: false }));
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to update paycheck");
      });
  };

  // When the user types a new hourly rate, update the annual ref so that a
  // subsequent hours-per-week change re-derives from the new rate.
  const handleSalaryChange = (val) => {
    setSalary(val);
    if (payType === "hourly") {
      hourlyAnnualRef.current =
        parseNum(val) * (parseNum(hoursPerWeek) || 40) * 52;
    }
  };

  const handleDisabledRoleClick = (option) => {
    setUpgradeSource("nursing_role_limit");
    if (option.requiredPlan === "Pro") {
      setUpgradeText({
        header: "Subscription Required!",
        desc: "",
        massage1: "",
        desc2: "You need a Pro plan to unlock this role",
        promassage:
          "Pro users see all 9 nursing roles, including NP, CRNA, and Midwife",
        propricem: "$2.99/month",
        propricey: "$29.99/year",
        plan: "Pro",
        lockedItemName: option.roleName,
      });
    }
    setUpgradeModalVisible(true);
  };

  const PcCustomRoleOption = (props) => {
    const { isDisabled, data } = props;
    return (
      <div
        onClick={(e) => {
          if (isDisabled) {
            e.stopPropagation();
            handleDisabledRoleClick(data);
          }
        }}
        style={{ width: "100%", cursor: isDisabled ? "pointer" : "default" }}
      >
        <SelectComponents.Option {...props}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
            }}
          >
            <span>{data.label}</span>
            {data.requiredPlan && (
              <span
                style={{
                  flexShrink: 0,
                  fontSize: "9px",
                  fontWeight: 800,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "var(--mmp-accent)",
                  background: "var(--mmp-accent-bg)",
                  border: "1px solid var(--mmp-accent-border)",
                  borderRadius: "14px",
                  padding: "1px 5px",
                }}
              >
                {data.requiredPlan}
              </span>
            )}
          </div>
        </SelectComponents.Option>
      </div>
    );
  };

  const applySavedToForm = (saved) => {
    if (!saved) return;

    // `saved.pay_by` and `saved.filing_status` now store machine codes
    // ("weekly", "mfj", …) — same enums the calculate endpoint uses.
    // PAY_BY_FROM_API / FILING_FROM_API stay as defensive fallbacks for any
    // legacy row that wasn't migrated.
    const freq =
      FREQ_PER_COUNT[saved.pay_by] !== undefined
        ? saved.pay_by
        : PAY_BY_FROM_API[saved.pay_by] || "biweekly";
    const perCount = FREQ_PER_COUNT[freq] || 1;
    // Prefer the stored annual_gross_salary (added so the saved scenario
    // matches the user's typed value exactly). Fall back to deriving from
    // gross_pay × paychecks-per-year for legacy rows saved before the
    // column existed.
    const storedAnnual = parseNum(saved.annual_gross_salary);
    const perPaycheckGross = parseNum(saved.gross_pay);
    const annualGross =
      storedAnnual > 0 ? storedAnnual : perPaycheckGross * perCount;

    const filingValue = FILING_STATUSES.some(
      (f) => f.value === saved.filing_status,
    )
      ? saved.filing_status
      : FILING_FROM_API[saved.filing_status] || "single";

    const nextDeductions = { k401: "0", hsa: "0", health: "0", fsa: "0" };
    Object.entries(saved.deductions || {}).forEach(([apiKey, info]) => {
      const localKey = DEDUCTION_FROM_API[apiKey];
      if (!localKey) return;
      nextDeductions[localKey] = String(info?.value ?? "0");
    });

    // Use the stored pay_type when present; fall back to inferring from
    // hours_per_week for older rows saved before the column existed.
    const nextPayType =
      saved.pay_type ||
      (parseNum(saved.hours_per_week) > 0 ? "hourly" : "salary");
    // For hourly mode the salary input holds the hourly rate, derived from
    // annual gross assuming the same 40 hrs/wk we save with.
    const nextSalary =
      nextPayType === "hourly"
        ? String(Math.round((annualGross / (40 * 52)) * 100) / 100)
        : String(Math.round(annualGross));
    const nextStateCode = saved.state || stateCode;

    // Restore the work city (and its state in the location card) so the
    // local-tax line re-appears on reload. The saved row stores the 2-letter
    // state code; map it back to the full name the location dropdown uses.
    const nextCity = saved.city || "";
    const nextPcState =
      pcApiStates.find((s) => s.code === nextStateCode)?.name || pcState;

    setPayType(nextPayType);
    setSalary(nextSalary);
    setFrequency(freq);
    setFiling(filingValue);
    setStateCode(nextStateCode);
    setDeductions(nextDeductions);
    setPcState(nextPcState);
    setPcCity(nextCity);

    setHoursPerWeek("40");
    setAppliedSnapshot(
      JSON.stringify({
        payType: nextPayType,
        salary: nextSalary,
        hoursPerWeek: "40",
        frequency: freq,
        filing: filingValue,
        stateCode: nextStateCode,
        pcCity: nextCity,
        deductions: nextDeductions,
      }),
    );
  };

  const currentSnapshot = useMemo(
    () =>
      JSON.stringify({
        payType,
        salary,
        hoursPerWeek,
        frequency,
        filing,
        stateCode,
        pcCity,
        deductions,
      }),
    [
      payType,
      salary,
      hoursPerWeek,
      frequency,
      filing,
      stateCode,
      pcCity,
      deductions,
    ],
  );

  const isDirty = Boolean(
    selectedSavedId && appliedSnapshot && currentSnapshot !== appliedSnapshot,
  );

  const handleViewPaycheck = (saved) => {
    if (!saved?.id) return;
    // Already loaded? Skip the form re-apply AND the touchView round-trip —
    // re-clicking the same card was firing /view repeatedly.
    if (selectedSavedId === saved.id) return;
    setSelectedSavedId(saved.id);
    applySavedToForm(saved.raw || saved);
    dispatch(viewPaycheck(saved.id));
  };

  const handleDeletePaycheck = (id) => {
    if (!id) return;
    dispatch(removePaycheck(id))
      .unwrap()
      .then((res) => {
        toast.success(res?.message || "Paycheck removed");
        if (selectedSavedId === id) setSelectedSavedId(null);
        if (savedScrollRef.current) savedScrollRef.current.scrollLeft = 0;
        dispatch(listPaychecks({ page: 1, limit: SAVED_LIMIT, append: false }));
      })
      .catch((err) => {
        toast.error(err?.message || "Failed to remove paycheck");
      });
  };

  return (
    <>
      <NoIndexSEO title={seoConfig.paycheck.title} />
      <div className="mmp-paycheck">
        <div className="shell">
          {activeProfileNudge && !profileNudgeDismissed && (
            <div
              className="paycheck-profile-nudge"
              role="button"
              tabIndex={0}
              onClick={() => navigate("/profile")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate("/profile");
                }
              }}
            >
              <div className="paycheck-profile-nudge-icon">
                <UserRound />
              </div>
              <div className="paycheck-profile-nudge-body">
                <div className="paycheck-profile-nudge-head">
                  <span className="paycheck-profile-nudge-eyebrow">
                    Complete your profile
                  </span>
                  <span className="paycheck-profile-nudge-pct">
                    {profileCompletionPercent}%
                  </span>
                </div>
                <div className="paycheck-profile-nudge-bar-track">
                  <div
                    className="paycheck-profile-nudge-bar-fill"
                    style={{ width: `${profileCompletionPercent}%` }}
                  />
                </div>
                <span className="paycheck-profile-nudge-text">
                  {activeProfileNudge.message}
                </span>
              </div>
              <button
                type="button"
                className="paycheck-profile-nudge-close"
                aria-label="Dismiss"
                onClick={(e) => {
                  e.stopPropagation();
                  dismissProfileNudge();
                }}
              >
                ×
              </button>
            </div>
          )}
          <div className="page-card">
          {/* ── Title bar ────────────────────────────── */}
          <div className="pc-title-bar">
            <div className="pc-title">Paycheck Calculator</div>
            <div className="pc-subtitle">
              See exactly what hits your bank account every payday.
            </div>
          </div>

          {/* ── Hero take-home ───────────────────────── */}
          <div className="pc-hero" data-tour="pc-hero">
            <div className="pc-hero-label">YOUR ESTIMATED TAKE-HOME</div>
            <div className="pc-hero-amount">
              {formatCurrency(calc.takeHome)}
            </div>
            <div className="pc-hero-meta">
              <span>per paycheck · {freqLabel(frequency)}</span>
              <span className="pc-pill pc-pill--success">
                {Math.round(calc.takeHomePct)}% take-home
              </span>
            </div>
          </div>

          {/* ── Free plan banner ────────────────────── */}
          {isFree && usageInfo && (
            <div className="pc-banner" role="status">
              <span className="dot" aria-hidden="true" />
              <span className="text">
                {displayUsed} of {displayLimit} free paycheck calculation
                {displayLimit === 1 ? "" : "s"} used today
              </span>
              <button
                type="button"
                className="upgrade-link"
                // onClick={() =>
                //   setCalcLimitInfo({
                //     planSlug,
                //     used: usageInfo?.used ?? 1,
                //     limit: usageInfo?.limit ?? 1,
                //     usageDate: usageInfo?.usage_date || "",
                //   })
                // }
                onClick={() => navigate("/profile/subscription")}
              >
                Upgrade <span aria-hidden="true">→</span>
              </button>
            </div>
          )}

          {/* ── Saved paychecks ─────────────────────── */}
          {(isListLoading || savedPaychecks.length > 0) && (
            <section className="pc-saved">
              <div className="pc-saved-head">
                <div className="pc-saved-head-left">
                  <span className="pc-saved-flag" aria-hidden="true">
                    <FlagIcon />
                  </span>
                  <h2 className="pc-saved-title">Saved paychecks</h2>
                  <span className="pc-saved-meta">
                    {isListLoading && !savedPaychecks.length
                      ? "Loading saved paychecks…"
                      : `You've saved ${paycheckTotal ||
                          savedPaychecks.length} paycheck calculation${
                          (paycheckTotal || savedPaychecks.length) === 1
                            ? ""
                            : "s"
                        }`}
                  </span>
                </div>
              </div>

              {savedPaychecks.length > 0 && (
                <div
                  className="pc-saved-scroll"
                  ref={savedScrollRef}
                  onScroll={handleSavedScroll}
                >
                  <div className="pc-saved-grid">
                    {savedPaychecks.map((p) => (
                      <div
                        className={`pc-saved-card ${
                          selectedSavedId === p.id ? "is-selected" : ""
                        }`}
                        key={p.id}
                        role="button"
                        tabIndex={0}
                        aria-pressed={selectedSavedId === p.id}
                        onClick={() => handleViewPaycheck(p)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleViewPaycheck(p);
                          }
                        }}
                      >
                        <div className="pc-saved-card-head">
                          <div className="pc-saved-card-title">
                            {p.customName ||
                              `${formatCurrency(p.salary)} Salary`}
                          </div>
                          <button
                            type="button"
                            className="pc-saved-card-more"
                            aria-label="Remove saved paycheck"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePaycheck(p.id);
                            }}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                        <div className="pc-saved-card-meta">
                          <span>
                            {capitalize(p.payByLabel || freqLabel(p.frequency))}{" "}
                            · Take-home: {formatCurrency2(p.takeHome)}
                          </span>
                          {p.date && (
                            <span className="pc-saved-card-date">{p.date}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* ── 2-col layout (filters left, results right on desktop) ── */}
          <div className="pc-layout">
            <aside className="pc-col-filters">
              {/* ── Location & role card ─────────────────── */}
              <section className="pc-card" data-tour="pc-location">
                <button
                  type="button"
                  className="pc-collapsible-header"
                  aria-expanded={locationOpen}
                  onClick={() => setLocationOpen((o) => !o)}
                >
                  <h2 className="pc-card-title">Your location &amp; role</h2>
                  <span
                    className={`pc-chevron ${locationOpen ? "is-open" : ""}`}
                  >
                    <ChevronDown />
                  </span>
                </button>
                {locationOpen && (
                  <div>
                    <SelectField
                      label="NURSING ROLE"
                      dataTour="pc-role"
                      options={pcRoleOptions}
                      value={pcRole}
                      onChange={setPcRole}
                      placeholder="Select role"
                      customComponents={{ Option: PcCustomRoleOption }}
                      isOptionDisabled={(o) => o.isDisabled}
                    />
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "12px",
                      }}
                    >
                      <SelectField
                        label="STATE"
                        dataTour="pc-state"
                        options={pcApiStates.map((s) => ({
                          value: s.name,
                          label: s.name,
                        }))}
                        value={pcState}
                        onChange={setPcState}
                        searchable
                        placeholder="Select state"
                      />
                      <SelectField
                        label="CITY"
                        dataTour="pc-city"
                        options={pcApiCities.map((c) => ({
                          value: c,
                          label: c,
                        }))}
                        value={pcCity}
                        onChange={setPcCity}
                        searchable
                        placeholder="Select city"
                      />
                    </div>
                  </div>
                )}
              </section>

              {/* ── Your salary card ────────────────────── */}
              <section className="pc-card" data-tour="pc-salary">
                <button
                  type="button"
                  className="pc-collapsible-header"
                  aria-expanded={salaryOpen}
                  onClick={() => setSalaryOpen((o) => !o)}
                >
                  <h2 className="pc-card-title">Your salary</h2>
                  <span className={`pc-chevron ${salaryOpen ? "is-open" : ""}`}>
                    <ChevronDown />
                  </span>
                </button>
                {salaryOpen && (
                  <div>
                    <PayTypeToggle
                      value={payType}
                      onChange={handlePayTypeChange}
                      dataTour="pc-paytype"
                    />
                    <MoneyField
                      dataTour="pc-annual-input"
                      label={
                        payType === "hourly"
                          ? "HOURLY RATE"
                          : "ANNUAL GROSS SALARY"
                      }
                      value={salary}
                      suffix={
                        payType === "hourly"
                          ? "/hr"
                          : payType === "salary"
                          ? "/yr"
                          : ""
                      }
                      onChange={handleSalaryChange}
                      autoBig
                    />
                    {payType === "hourly" && (
                      <div className="pc-field">
                        <div className="pc-field-label">HOURS PER WEEK</div>
                        <div className="pc-money-input">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={hoursPerWeek}
                            onChange={(e) => {
                              const v = e.target.value.replace(/[^\d.]/g, "");
                              if (parseFloat(v) > 168) return;
                              setHoursPerWeek(v);
                            }}
                            onBlur={() => {
                              if (
                                !hoursPerWeek ||
                                parseFloat(hoursPerWeek) <= 0
                              )
                                setHoursPerWeek("40");
                            }}
                            placeholder="40"
                          />
                          <span className="pc-money-suffix">hrs/wk</span>
                        </div>
                      </div>
                    )}
                    <SelectField
                      label="PAY FREQUENCY"
                      dataTour="pc-freq"
                      options={PAY_FREQUENCIES}
                      value={frequency}
                      onChange={setFrequency}
                      placeholder="Select pay frequency"
                    />
                    <SelectField
                      label="FILING STATUS"
                      dataTour="pc-filing"
                      options={FILING_STATUSES}
                      value={filing}
                      onChange={setFiling}
                      placeholder="Select filing status"
                    />
                  </div>
                )}
              </section>

              {/* ── Pre-tax deductions ───────────────────── */}
              <section className="pc-card" data-tour="pc-ded">
                <button
                  type="button"
                  className="pc-collapsible-header"
                  aria-expanded={deductionsOpen}
                  onClick={() => setDeductionsOpen((o) => !o)}
                >
                  <h2 className="pc-card-title">
                    Pre-tax deductions{" "}
                    <span className="pc-optional">(optional)</span>
                  </h2>
                  <span
                    className={`pc-chevron ${deductionsOpen ? "is-open" : ""}`}
                  >
                    <ChevronDown />
                  </span>
                </button>

                {deductionsOpen && (
                  <div className="pc-deductions">
                    {PRE_TAX_DEDUCTIONS.map((d) => {
                      // Dynamic HSA help text based on filing status
                      let helpText = d.help;
                      if (d.key === "hsa") {
                        const hsaCap = HSA_CAPS[filing] || HSA_CAPS.single;
                        const coverageType =
                          filing === "single" ? "individual" : "family";
                        helpText = `${currentYear} max: $${hsaCap.toLocaleString()} ${coverageType} coverage ($1,000 catch-up if 55+).`;
                      }

                      return (
                        <div
                          className="pc-deduction"
                          key={d.key}
                          data-tour={`pc-${d.key}`}
                        >
                          <div className="pc-deduction-label">{d.label}</div>
                          <div className="pc-money-input">
                            <span className="pc-money-prefix">$</span>
                            <input
                              type="number"
                              inputMode="decimal"
                              min="0"
                              value={deductions[d.key]}
                              onChange={(e) => {
                                // Cap at the per-paycheck IRS limit (annual cap ÷
                                // paychecks/year) once a pay frequency is set.
                                let v = e.target.value;

                                // For HSA, use filing-status-dependent cap
                                let cap = ANNUAL_CONTRIBUTION_CAPS[d.key];
                                if (d.key === "hsa") {
                                  cap = HSA_CAPS[filing] || HSA_CAPS.single;
                                }

                                const perYear = FREQ_PER_COUNT[frequency];
                                if (cap && perYear) {
                                  const max =
                                    Math.floor((cap / perYear) * 100) / 100;
                                  if (parseFloat(v) > max) v = String(max);
                                }
                                setDeductions((p) => ({ ...p, [d.key]: v }));
                              }}
                              onWheel={(e) => e.currentTarget.blur()}
                              placeholder="0"
                            />
                            <span className="pc-money-suffix">/paycheck</span>
                          </div>
                          <div className="pc-deduction-help">{helpText}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* ── Calculate trigger ────────────────────
                Explicit user action so each POST aligns with the daily limit
                — typing into a field never burns the quota. */}
              <button
                type="button"
                className="pc-btn pc-btn--primary pc-calc-btn"
                onClick={handleCalcClick}
                disabled={isCalculating}
                // style={{ display: "none" }}
              >
                {isCalculating
                  ? "Calculating…"
                  : limitReached
                  ? "Daily limit reached"
                  : "Calculate"}
              </button>
              {showCalcMissing && !isReady && (
                <p className="pc-required-warning" role="status">
                  {missingMsg}
                </p>
              )}
            </aside>

            <main className="pc-col-results">
              {/* ── Per paycheck breakdown ──────────────── */}
              <section className="pc-card" data-tour="pc-breakdown">
                <div className="pc-breakdown-head">
                  <h2 className="pc-card-title">
                    Per paycheck breakdown
                    <InfoTip text="Estimated figures based on the current-year federal, state and (where applicable) local tax brackets. Actual withholding can vary with your W-4 and employer setup." />
                  </h2>
                  <span className="pc-badge">
                    {freqLabel(frequency).toUpperCase()}
                  </span>
                </div>

                {/* Banner when the selected state isn't in our DB — we still
                  show federal + FICA but skip the state row to avoid
                  displaying a misleading $0. */}
                {apiCalculation && !stateSupported && (
                  <div className="pc-coverage-note">
                    Federal + FICA only — state-level estimates not yet
                    available for {stateCode}.
                  </div>
                )}

                <div className="pc-breakdown-grid">
                  <div className="pc-breakdown-col">
                    <BreakdownRow
                      k="Gross pay"
                      v={formatCurrency(calc.grossPaycheck)}
                      bold
                    />
                    {calc.fed > 0 && (
                      <BreakdownRow
                        k="Federal tax"
                        v={formatCurrency(calc.fed)}
                        pct={`${calc.fedPct.toFixed(2)}%`}
                        info="Progressive federal income tax applied to your taxable income (gross pay minus pre-tax deductions and the federal standard deduction)."
                      />
                    )}
                    {stateSupported && calc.state > 0 && (
                      <BreakdownRow
                        k="State tax"
                        v={formatCurrency(calc.state)}
                        pct={`${calc.statePct.toFixed(2)}%`}
                        info="State income tax on your taxable income, using your work state's tax brackets."
                      />
                    )}
                    {localSupported && calc.local > 0 && (
                      <BreakdownRow
                        k={`${localCity || "Local"} local tax`}
                        v={formatCurrency(calc.local)}
                        pct={`${calc.localPct.toFixed(2)}%`}
                        info={`City income tax for ${localCity ||
                          "your work city"}, charged by some cities on top of state tax (resident rate).`}
                      />
                    )}
                    {calc.ss > 0 && (
                      <BreakdownRow
                        k="Social Security"
                        v={formatCurrency(calc.ss)}
                        pct={`${calc.ssPct.toFixed(2)}%`}
                        info="6.2% of your wages, up to the yearly Social Security wage cap. Earnings above the cap aren't taxed for Social Security."
                      />
                    )}
                    {calc.medicare > 0 && (
                      <BreakdownRow
                        k="Medicare"
                        v={formatCurrency(calc.medicare)}
                        pct={`${calc.medicarePct.toFixed(2)}%`}
                        info="1.45% on all wages, plus an extra 0.9% on wages above the IRS threshold for your filing status."
                      />
                    )}
                    {stateInsuranceSupported && calc.sdi > 0 && (
                      <BreakdownRow
                        k={`${stateCode} state insurance`}
                        v={formatCurrency(calc.sdi)}
                        pct={`${calc.sdiPct.toFixed(2)}%`}
                        info="State disability / family-leave insurance, withheld in the states that require it."
                      />
                    )}
                  </div>
                  <div className="pc-breakdown-col">
                    {calc.preTax > 0 && (
                      <BreakdownRow
                        k="Pre-tax deductions"
                        v={formatCurrency(calc.preTax)}
                      />
                    )}
                    {calc.postTax > 0 && (
                      <BreakdownRow
                        k="Post-tax deductions"
                        v={formatCurrency(calc.postTax)}
                      />
                    )}
                    <BreakdownRow
                      k="Total taxes"
                      v={formatCurrency(calc.totalTaxes)}
                      bold
                      danger
                      info="Federal + state + any local income tax, plus Social Security, Medicare and state insurance."
                    />
                    <BreakdownRow
                      k="Take-home"
                      v={formatCurrency(calc.takeHome)}
                      bold
                      success
                      info="Gross pay minus all taxes and your pre- and post-tax deductions."
                    />
                  </div>
                </div>
              </section>

              {/* ── Where your money goes (donut) ───────── */}
              <section className="pc-card" data-tour="pc-donut">
                <h2 className="pc-card-title">Where your money goes</h2>
                {(() => {
                  const donutSegments = [
                    {
                      name: "Take-home",
                      value: calc.takeHome,
                      color: "#16A54A",
                      pct: calc.takeHomePct,
                    },
                    {
                      name: "Federal tax",
                      value: calc.fed,
                      // Near-black navy — nearly invisible against the dark
                      // theme's card surface, so lighten it in dark mode.
                      color: resolvedTheme === "dark" ? "#5B7A9E" : "#0E2A47",
                      pct: calc.fedPct,
                    },
                    ...(stateSupported
                      ? [
                          {
                            name: "State tax",
                            value: calc.state,
                            color: "#0395D3",
                            pct: calc.statePct,
                          },
                        ]
                      : []),
                    ...(localSupported && calc.local > 0
                      ? [
                          {
                            name: `${localCity || "Local"} local tax`,
                            value: calc.local,
                            color: "#7C3AED",
                            pct: calc.localPct,
                          },
                        ]
                      : []),
                    {
                      name: "FICA",
                      value: calc.ss + calc.medicare,
                      color: "#E97D2E",
                      pct: calc.ssPct + calc.medicarePct,
                    },
                    ...(stateInsuranceSupported && calc.sdi > 0
                      ? [
                          {
                            name: "State insurance",
                            value: calc.sdi,
                            color: "#A8DBF0",
                            pct: calc.sdiPct,
                          },
                        ]
                      : []),
                  ].filter((seg) => seg.value > 0);
                  return (
                    <div className="pc-donut-wrap">
                      <Donut
                        centerLabel={`${Math.round(calc.takeHomePct)}%`}
                        centerSub="take-home"
                        formatValue={formatCurrency}
                        segments={donutSegments}
                        hover={donutHover}
                        onHover={setDonutHover}
                      />
                      <ul className="pc-legend">
                        {donutSegments.map((seg, idx) => (
                          <LegendItem
                            key={seg.name}
                            idx={idx}
                            color={seg.color}
                            name={seg.name}
                            pct={seg.pct}
                            value={seg.value}
                            isHover={donutHover === idx}
                            isDim={donutHover !== null && donutHover !== idx}
                            onHover={setDonutHover}
                          />
                        ))}
                      </ul>
                    </div>
                  );
                })()}
              </section>

              {/* ── Annual summary ──────────────────────── */}
              <section className="pc-card" data-tour="pc-annual">
                <h2 className="pc-card-title">
                  Annual summary
                  <InfoTip text="Your per-paycheck figures multiplied out across the year (paychecks per year depends on your pay frequency)." />
                </h2>
                <div className="pc-summary-grid">
                  <SummaryStat
                    label="GROSS ANNUAL"
                    value={formatCurrency(calc.annualGross)}
                  />
                  <SummaryStat
                    label="NET ANNUAL"
                    value={formatCurrency(calc.netAnnual)}
                    tone="success"
                  />
                  <SummaryStat
                    label="TOTAL TAXES"
                    value={formatCurrency(calc.totalTaxAnnual)}
                    tone="danger"
                  />
                  <SummaryStat
                    label="EFFECTIVE TAX RATE"
                    value={`${calc.effectivePct.toFixed(1)}%`}
                    info="Total annual taxes ÷ gross annual pay — your overall tax rate, not your top bracket."
                  />
                </div>
              </section>

              {/* ── Actions ─────────────────────────────── */}
              {/* Compare Cities removed until the dedicated compare flow is built;
                Save paycheck is now the primary, full-width action (the
                `pc-btn--primary` rule in paycheck.scss spans the full row). */}
              <div className="pc-actions" data-tour="pc-ctas">
                {!selectedSavedId && (
                  <button
                    type="button"
                    className="pc-btn pc-btn--primary"
                    onClick={openSaveNameModal}
                    disabled={isSaving}
                  >
                    <SaveIcon /> {isSaving ? "Saving…" : "Save paycheck"}
                  </button>
                )}
                {selectedSavedId && isDirty && (
                  <>
                    <button
                      type="button"
                      className="pc-btn pc-btn--outline"
                      onClick={() => {
                        if (!isReady) {
                          setShowSaveMissing(true);
                          return;
                        }
                        handleUpdatePaycheck();
                      }}
                      disabled={isSaving}
                    >
                      <SaveIcon /> {isSaving ? "Updating…" : "Update paycheck"}
                    </button>
                    <button
                      type="button"
                      className="pc-btn pc-btn--outline"
                      onClick={openSaveNameModal}
                      disabled={isSaving}
                    >
                      <SaveIcon /> Save as new
                    </button>
                  </>
                )}
                {selectedSavedId && !isDirty && (
                  <button
                    type="button"
                    className="pc-btn pc-btn--primary is-saved"
                    disabled
                  >
                    <CheckIcon /> Saved
                  </button>
                )}
              </div>
              {showSaveMissing && !isReady && !isSaving && (
                <p className="pc-required-warning" role="status">
                  {missingMsg}
                </p>
              )}

              <p className="pc-footnote">
                This calculator uses {new Date().getFullYear()} federal and
                state tax brackets. Results are estimates and may not reflect
                your exact paycheck.
              </p>
            </main>
          </div>
          </div>{/* /page-card */}
        </div>

        {/* Name-this-paycheck modal — prompts the user before saving so they
          can label the scenario (e.g. "Kaiser RN offer"). Prefilled with
          "$<annual gross> paycheck · <today>"; user can edit or accept. */}
        <Modal
          show={nameModalOpen}
          onHide={() => !isSaving && setNameModalOpen(false)}
          centered
          backdrop="static"
          dialogClassName="pc-name-modal"
          contentClassName="pc-name-modal__content"
        >
          <Modal.Body className="pc-name-modal__body">
            {/* Close button — top right corner */}
            <button
              type="button"
              aria-label="Close"
              className="pc-name-modal__close"
              onClick={() => !isSaving && setNameModalOpen(false)}
              disabled={isSaving}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            {/* Icon / illustration at top */}
            <div className="pc-name-modal__icon" aria-hidden="true">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
              </svg>
            </div>

            <h3 className="pc-name-modal__title">Name your paycheck</h3>
            <p className="pc-name-modal__subtitle">
              Give it a label you'll recognise later — like a job offer or
              hypothetical scenario.
            </p>

            <div className="pc-name-modal__field">
              <label className="pc-name-modal__label" htmlFor="pc-name-input">
                Custom name
              </label>
              <input
                id="pc-name-input"
                autoFocus
                type="text"
                className="pc-name-modal__input"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                placeholder="e.g. Kaiser RN offer"
                maxLength={120}
                disabled={isSaving}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !isSaving) handleSavePaycheck();
                }}
              />
              <div className="pc-name-modal__hint">
                Up to 120 characters · You can rename it later
              </div>
            </div>

            <div className="pc-name-modal__actions">
              <button
                type="button"
                className="pc-name-modal__btn pc-name-modal__btn--ghost"
                onClick={() => setNameModalOpen(false)}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="pc-name-modal__btn pc-name-modal__btn--primary"
                onClick={handleSavePaycheck}
                disabled={isSaving || !(nameDraft || "").trim()}
              >
                {isSaving ? "Saving…" : "Save paycheck"}
              </button>
            </div>
          </Modal.Body>
        </Modal>

        {/* Pro is the top paid tier — there's no higher plan to upsell, so a
          Pro user hitting the hard scenario cap gets a status-only modal
          ("delete to make room"). Free / Plus users still get the upgrade
          flow since Pro removes their limit. */}
        <RestrictionModal
          isVisible={Boolean(limitInfo)}
          onClose={() => setLimitInfo(null)}
          eventSource="paycheck_scenario_limit"
          iconType="clipboard-dollar"
          heading={
            limitInfo?.limitType === "daily"
              ? "You've saved your free paycheck for today"
              : limitInfo?.planSlug === "pro"
              ? "Maximum saved scenarios reached"
              : "You've reached your saved scenario limit"
          }
          subText={
            limitInfo?.limitType === "daily"
              ? "Free plans can save 1 paycheck per day. Upgrade to Pro to save unlimited paychecks."
              : limitInfo?.planSlug === "pro"
              ? `Pro plans cap saved scenarios at ${limitInfo?.planLimit ??
                  25}. Delete an existing paycheck to save a new one.`
              : "Delete an existing paycheck scenario to save a new one, or upgrade to Pro for more scenarios."
          }
          infoText={
            limitInfo?.limitType === "daily"
              ? `You're on the ${capitalize(
                  limitInfo?.planSlug || "free",
                )} plan · ${limitInfo?.usedToday ??
                  1} of ${limitInfo?.dailyLimit ?? 1} daily saves used`
              : `You're on the ${capitalize(
                  limitInfo?.planSlug || "free",
                )} plan · ${limitInfo?.totalSaved ??
                  0} of ${limitInfo?.planLimit ?? 1} paycheck scenarios used`
          }
          thirdCtaMode="newsletter"
          hideUpgradeCtas={limitInfo?.planSlug === "pro"}
        />

        <RestrictionModal
          isVisible={Boolean(calcLimitInfo)}
          onClose={() => setCalcLimitInfo(null)}
          eventSource="paycheck_calculator_limit"
          iconType="calculator"
          heading="You've reached your daily calculation limit"
          subText={`You've used your ${calcLimitInfo?.limit ??
            1} free paycheck calculation for today. Upgrade to Pro for unlimited calculations.`}
          infoText={`You're on the ${capitalize(
            calcLimitInfo?.planSlug || "free",
          )} plan · ${calcLimitInfo?.used ?? 0} of ${calcLimitInfo?.limit ??
            1} calculations used today`}
          thirdCtaMode="newsletter"
        />

        <ComparisonModal
          title={upgradeText?.header}
          description={upgradeText.desc}
          massage1={upgradeText.massage1}
          pluspricem={upgradeText.pluspricem}
          pluspricey={upgradeText.pluspricey}
          prodesc={upgradeText.desc2}
          promassage={upgradeText.promassage}
          propricem={upgradeText.propricem}
          propricey={upgradeText.propricey}
          plan={upgradeText.plan}
          lockedItemName={upgradeText.lockedItemName}
          eventSource={upgradeSource}
          isVisible={upgradeModalVisible}
          onClose={() => setUpgradeModalVisible(false)}
          onPressYes={() => {
            navigate("/profile/subscription");
            setUpgradeModalVisible(false);
          }}
          yesText="Upgrade Now"
          noText="Cancel"
          extraMetaInfo={true}
        />
      </div>
    </>
  );
};

/* ── tiny helpers / icons used by JSX above ─────────────────── */

function freqLabel(value) {
  const labels = {
    weekly: "weekly",
    biweekly: "bi-weekly",
    semimonthly: "semi-monthly",
    monthly: "monthly",
    annual: "annually",
  };
  return labels[value] || value;
}

function capitalize(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function BreakdownRow({ k, v, pct, bold, danger, success, info }) {
  const cls = [
    "pc-br-row",
    bold ? "is-bold" : "",
    danger ? "is-danger" : "",
    success ? "is-success" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={cls}>
      <span className="k">
        {k}
        {info && <InfoTip text={info} />}
      </span>
      <span className="v">
        {v}
        {pct && <span className="pct">{pct}</span>}
      </span>
    </div>
  );
}

function LegendItem({ idx, color, name, pct, value, isHover, isDim, onHover }) {
  const cls = [
    "pc-legend-item",
    isHover ? "is-hover" : "",
    isDim ? "is-dim" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <li
      className={cls}
      onMouseEnter={() => onHover && onHover(idx)}
      onMouseLeave={() => onHover && onHover(null)}
    >
      <span className="pc-legend-dot" style={{ background: color }} />
      <div>
        <div className="pc-legend-name">{name}</div>
        <div className="pc-legend-meta">
          {pct.toFixed(1)}% · {formatCurrency(value)}
        </div>
      </div>
    </li>
  );
}

function SummaryStat({ label, value, tone, info }) {
  return (
    <div className={`pc-stat ${tone ? `pc-stat--${tone}` : ""}`}>
      <div className="pc-stat-label">
        {label}
        {info && <InfoTip text={info} />}
      </div>
      <div className="pc-stat-value">{value}</div>
    </div>
  );
}

function SaveIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function FlagIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
      <path d="M6 2h12a1 1 0 0 1 1 1v19a1 1 0 0 1-1.6.8L12 18.25l-5.4 4.55A1 1 0 0 1 5 22V3a1 1 0 0 1 1-1z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

export default Paycheck;
